import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';

/**
 * Global audio playback context for SHS Topics.
 * Holds a single <audio> reference (managed inside FloatingAudioPlayer) and
 * exposes play / pause / seek / track-change to any component on the page.
 *
 * Audio analytics are emitted from the controlling component (TopicMediaTabs)
 * to keep this context pure.
 */

const AudioPlayerContext = createContext(null);

export const useAudioPlayer = () => {
  const ctx = useContext(AudioPlayerContext);
  if (!ctx) throw new Error('useAudioPlayer must be used inside AudioPlayerProvider');
  return ctx;
};

export const AudioPlayerProvider = ({ children }) => {
  // Active track metadata
  const [track, setTrack] = useState(null);          // { topicId, topicTitle, language, src, duration, mode }
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [previewLimitSec, setPreviewLimitSec] = useState(null); // null = unlimited
  const [previewBlocked, setPreviewBlocked] = useState(false);

  // Listeners for analytics ("hooks")
  const listenersRef = useRef([]);

  const subscribe = useCallback((fn) => {
    listenersRef.current.push(fn);
    return () => {
      listenersRef.current = listenersRef.current.filter(f => f !== fn);
    };
  }, []);

  const emit = useCallback((evt) => {
    listenersRef.current.forEach(fn => { try { fn(evt); } catch { /* swallow */ } });
  }, []);

  // Imperative controls (assigned by FloatingAudioPlayer when it mounts the <audio>)
  const controlsRef = useRef({
    play: () => {},
    pause: () => {},
    seek: () => {},
    stop: () => {},
  });

  const registerControls = useCallback((controls) => {
    controlsRef.current = controls;
  }, []);

  const loadTrack = useCallback((newTrack, options = {}) => {
    setTrack(newTrack);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(newTrack?.duration || 0);
    setPreviewLimitSec(options.previewLimitSec ?? null);
    setPreviewBlocked(false);
  }, []);

  const play = useCallback(() => controlsRef.current.play(), []);
  const pause = useCallback(() => controlsRef.current.pause(), []);
  const seek = useCallback((t) => controlsRef.current.seek(t), []);
  const stop = useCallback(() => {
    controlsRef.current.stop();
    setTrack(null);
    setIsPlaying(false);
    setPreviewBlocked(false);
  }, []);

  const value = {
    track, isPlaying, currentTime, duration,
    previewLimitSec, previewBlocked,
    setIsPlaying, setCurrentTime, setDuration, setPreviewBlocked,
    loadTrack, play, pause, seek, stop,
    registerControls,
    subscribe, emit,
  };

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
    </AudioPlayerContext.Provider>
  );
};
